import React, { useState } from "react";
import AdminSideDrawer, { DrawerFormBody } from "../admin/AdminSideDrawer";
import { InlineDatePickerField, InlineDatePickerPanel } from "../DatePickerField";

const todayIso = () => new Date().toISOString().slice(0, 10);

const DATE_TRIGGER =
  "!rounded-xl !bg-white !min-h-[45px] !h-[45px] !py-0";

/**
 * Add task drawer — title + due date (same calendar as Add invoice).
 * Optional `clients` list adds an engagement picker for admin Home.
 */
export default function AddTaskDrawer({
  open,
  onClose,
  onSubmit,
  eyebrow = "Workboard",
  title = "Add task",
  scopeLabel = null,
  clients = null,
  initialDue = null,
  testId = "add-task-drawer",
}) {
  const [draft, setDraft] = useState("");
  const [due, setDue] = useState(initialDue || todayIso());
  const [clientId, setClientId] = useState("");
  const [dateOpen, setDateOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const needsClient = Array.isArray(clients) && clients.length > 0;
  const canSubmit = Boolean(draft.trim()) && (!needsClient || clientId);

  const submit = async (e) => {
    e.preventDefault();
    const label = draft.trim();
    if (!label || busy) return;
    if (needsClient && !clientId) return;
    setBusy(true);
    try {
      await onSubmit?.({
        title: label,
        due: due || todayIso(),
        ...(needsClient ? { clientId } : {}),
      });
      setDraft("");
      setDue(todayIso());
      setClientId("");
      setDateOpen(false);
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminSideDrawer
      open={open}
      onClose={onClose}
      eyebrow={eyebrow}
      title={title}
      testId={testId}
      footer={
        <>
          <button type="button" onClick={onClose} className="t-btn-secondary !rounded-xl">
            Cancel
          </button>
          <button
            type="submit"
            form="add-task-form"
            className="neo-cta"
            disabled={busy || !canSubmit}
            data-testid={`${testId}-submit`}
          >
            {busy ? "Saving…" : "Add"}
          </button>
        </>
      }
    >
      <form id="add-task-form" className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
        <DrawerFormBody>
          {scopeLabel ? (
            <p className="text-xs text-[var(--t-meta)]">
              Scope · <span className="text-[var(--t-text)]">{scopeLabel}</span>
            </p>
          ) : null}
          {needsClient ? (
            <div>
              <label className="label-eyebrow mb-2 block">Engagement</label>
              <select
                className="t-select !rounded-xl !bg-white w-full appearance-none !px-4"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                data-testid={`${testId}-client`}
                required
              >
                <option value="">Select engagement</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div>
            <label className="label-eyebrow mb-2 block">Task</label>
            <input
              autoFocus={!needsClient}
              className="t-input !rounded-xl !bg-white"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="What needs doing?"
              required
              data-testid={`${testId}-title`}
            />
          </div>
          <div>
            <label className="label-eyebrow mb-2 block">Due</label>
            <InlineDatePickerField
              value={due}
              onChange={setDue}
              placeholder="Select date"
              triggerClassName={DATE_TRIGGER}
              expanded={dateOpen}
              onExpandedChange={setDateOpen}
              detachPanel
              data-testid={`${testId}-due`}
            />
            {dateOpen ? (
              <InlineDatePickerPanel
                value={due}
                onChange={setDue}
                onClose={() => setDateOpen(false)}
                className="mt-2"
                data-testid={`${testId}-due-panel`}
              />
            ) : null}
          </div>
        </DrawerFormBody>
      </form>
    </AdminSideDrawer>
  );
}

export function formatDueLabel(iso) {
  if (!iso) return "—";
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function isDueToday(iso) {
  if (!iso) return false;
  const day = String(iso).slice(0, 10);
  return day === todayIso();
}
